
import Vue from "vue"
import axios from "axios"
import { EventBus } from "@/plugins/eventBus"
import { hasPermission } from "@/plugins/token"
import { state } from "@/plugins/state"
import { isValidEtestingTenantUrl, normalizeEtestingApiUri } from "@/utils/etestingApiUri"

type ETestingStatus = {
  enabled?: boolean
  connected?: boolean
  space_id?: number | null
  api_uri?: string | null
  user_code?: string | null
  util_id?: string | null
  client_log?: string | null
  filiale_id?: string | null
  has_client_password?: boolean
}

type ETestingFormState = {
  enabled: boolean
  api_uri: string
  client_log: string
  client_pas: string
  filiale_id: string
  user_code: string
}

export default Vue.extend({
  name: "ETestingConnectionCard",
  data() {
    return {
      statusLoaded: false,
      connected: false,
      hasClientPassword: false,
      dialogOpen: false,
      saving: false,
      connecting: false,
      disconnecting: false,
      form: {
        enabled: false,
        api_uri: "",
        client_log: "",
        client_pas: "",
        filiale_id: "",
        user_code: "",
      },
      dialogFormSnapshot: null as ETestingFormState | null,
      dialogHasClientPasswordSnapshot: false,
    }
  },
  computed: {
    visible(): boolean {
      return hasPermission("evaluations")
    },
    spaceId(): number | null {
      const raw = state.agency
      if (raw === null || raw === undefined || raw === "") {
        return null
      }
      const parsed = Number(raw)
      return Number.isFinite(parsed) ? parsed : null
    },
    clientUrlRules(): Array<(value: string) => boolean | string> {
      const invalidMessage = this.$vuetify.lang.t("$vuetify.etesting.invalid_client_url")
      return [
        (value: string) => isValidEtestingTenantUrl(value) || invalidMessage,
      ]
    },
  },
  created() {
    if (this.visible) {
      this.fetchStatus()
    }
  },
  methods: {
    snapshotForm(): ETestingFormState {
      return {
        enabled: this.form.enabled,
        api_uri: this.form.api_uri,
        client_log: this.form.client_log,
        client_pas: this.form.client_pas,
        filiale_id: this.form.filiale_id,
        user_code: this.form.user_code,
      }
    },
    restoreForm(snapshot: ETestingFormState): void {
      this.form.enabled = snapshot.enabled
      this.form.api_uri = snapshot.api_uri
      this.form.client_log = snapshot.client_log
      this.form.client_pas = snapshot.client_pas
      this.form.filiale_id = snapshot.filiale_id
      this.form.user_code = snapshot.user_code
    },
    openDialog(): void {
      this.dialogFormSnapshot = this.snapshotForm()
      this.dialogHasClientPasswordSnapshot = this.hasClientPassword
      this.dialogOpen = true
    },
    applyStatus(data: ETestingStatus): void {
      this.form.enabled = data.enabled === true
      this.form.api_uri = data.api_uri ?? ""
      this.form.user_code = data.user_code ?? data.util_id ?? ""
      this.form.client_log = data.client_log ?? ""
      this.form.filiale_id = data.filiale_id ?? ""
      this.connected = data.connected === true
      this.hasClientPassword = data.has_client_password === true
      this.statusLoaded = true
    },
    cancelDialog(): void {
      if (this.dialogFormSnapshot) {
        this.restoreForm(this.dialogFormSnapshot)
        this.hasClientPassword = this.dialogHasClientPasswordSnapshot
      }
      this.dialogOpen = false
      this.dialogFormSnapshot = null
    },
    async fetchStatus(): Promise<void> {
      try {
        const params = this.spaceId !== null ? { space_id: this.spaceId } : {}
        const response = await axios.get<ETestingStatus>("/v1/etesting/status", { params })
        this.applyStatus(response.data ?? {})
        this.$emit("connection-changed")
      } catch (error) {
        EventBus.$emit("snackbar", { axiosError: error })
      }
    },
    validateClientUrl(): boolean {
      if (!isValidEtestingTenantUrl(this.form.api_uri)) {
        EventBus.$emit("snackbar", {
          message: this.$vuetify.lang.t("$vuetify.etesting.invalid_client_url"),
          color: "error",
        })
        return false
      }
      return true
    },
    buildPayload(): Record<string, unknown> {
      const payload: Record<string, unknown> = {
        enabled: true,
      }
      if (this.spaceId !== null) {
        payload.space_id = this.spaceId
      }
      const apiUri = normalizeEtestingApiUri(this.form.api_uri)
      payload.api_uri = apiUri === "" ? "" : apiUri
      payload.user_code = this.form.user_code
      payload.client_log = this.form.client_log
      payload.filiale_id = this.form.filiale_id
      if (this.form.client_pas !== "") {
        payload.client_pas = this.form.client_pas
      }
      return payload
    },
    async save(options?: { silent?: boolean }): Promise<boolean> {
      if (!this.validateClientUrl()) {
        return false
      }
      this.saving = true
      let saved = false
      try {
        const response = await axios.put<ETestingStatus>("/v1/etesting/connection", this.buildPayload())
        const data = response.data ?? {}
        this.applyStatus(data)
        this.form.api_uri = data.api_uri ?? normalizeEtestingApiUri(this.form.api_uri)
        this.form.client_pas = ""
        this.dialogFormSnapshot = this.snapshotForm()
        this.dialogHasClientPasswordSnapshot = this.hasClientPassword
        if (!options?.silent) {
          EventBus.$emit("snackbar", {
            message: this.$vuetify.lang.t("$vuetify.etesting.saved"),
            color: "success",
          })
        }
        this.$emit("connection-changed")
        saved = true
      } catch (error) {
        EventBus.$emit("snackbar", { axiosError: error })
      } finally {
        this.saving = false
      }
      return saved
    },
    async connectFromModal(): Promise<void> {
      if (!this.validateClientUrl()) {
        return
      }
      this.connecting = true
      try {
        const saved = await this.save({ silent: true })
        if (!saved) {
          return
        }
        const body = this.spaceId !== null ? { space_id: this.spaceId } : {}
        const testResponse = await axios.post<ETestingStatus>("/v1/etesting/connection/test", body)
        this.applyStatus(testResponse.data ?? {})
        EventBus.$emit("snackbar", {
          message: this.$vuetify.lang.t("$vuetify.etesting.test_success"),
          color: "success",
        })
        this.dialogFormSnapshot = null
        this.dialogOpen = false
        this.$emit("connection-changed")
      } catch (error) {
        await this.fetchStatus()
        EventBus.$emit("snackbar", { axiosError: error })
      } finally {
        this.connecting = false
      }
    },
    async disconnect(): Promise<void> {
      this.disconnecting = true
      try {
        const params = this.spaceId !== null ? { space_id: this.spaceId } : {}
        const response = await axios.delete<ETestingStatus>("/v1/etesting/connection", { params })
        this.applyStatus(response.data ?? {})
        this.form.client_pas = ""
        this.dialogOpen = false
        this.dialogFormSnapshot = null
        EventBus.$emit("snackbar", {
          message: this.$vuetify.lang.t("$vuetify.etesting.disconnected"),
          color: "success",
        })
        this.$emit("connection-changed")
      } catch (error) {
        EventBus.$emit("snackbar", { axiosError: error })
      } finally {
        this.disconnecting = false
      }
    },
  },
})
